import { Patientportal_AppointmentProxyMsg } from '@/generated/graphql';
import { CalendarOutlined, ClockCircleOutlined, TeamOutlined } from '@ant-design/icons';
import { Avatar, Empty, List } from 'antd';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useRouter } from 'next/router';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import {
  buildTimestampDate,
  buildTimestampTime,
  extractTranslation,
  formatPortalFacilityPath,
} from '../../helpers/formatters';
import {
  AppointmentLocation,
  buildImageDownloadUrl,
  getAppointmentTypeName,
  getAttendeeStatus,
  useCurrentUser,
} from '../../helpers/utils';
import { AppointmentFilterType } from '../../pages/appointments';
import styles from '../../pages/appointments/appointments.module.scss';
import { AveliosButton } from '../common-layout/avelios-button';
import { AppointmentStatusTag } from './appointment-status-tag';

interface AppointmentListProps {
  appointmentFilter: AppointmentFilterType;
  proxyAppointments: Maybe<(Maybe<Patientportal_AppointmentProxyMsg> | undefined)[]> | undefined;
  appointmentId: Maybe<string> | undefined;
  setAppointmentId: (appointmentProxy: Maybe<string> | undefined) => void;
  routedAppointment?: Maybe<string>;
  loadMore?: () => void;
}

export const AppointmentList: FC<AppointmentListProps> = ({
  appointmentFilter,
  proxyAppointments,
  appointmentId,
  setAppointmentId,
  loadMore,
}) => {
  const { formatMessage } = useIntl();

  const router = useRouter();

  const { currentUser } = useCurrentUser();

  return proxyAppointments && proxyAppointments?.length > 0 ? (
    <List<Patientportal_AppointmentProxyMsg>
      dataSource={proxyAppointments?.map((pA) => pA!) || []}
      loadMore={
        loadMore && (
          <AveliosButton onClick={loadMore} type={'link'} block>
            {formatMessage({ id: 'appointments.load-more' })}
          </AveliosButton>
        )
      }
      renderItem={(item) => {
        const facilities = item?.portalFacilityPath?.portalFacilities;
        const officeHour = facilities?.[facilities.length - 1];

        const appointment = item?.appointment;

        const startTime = appointment?.startTime;
        const selected = appointmentId === item.appointment?.id;

        const appointmentTypeName = extractTranslation(
          getAppointmentTypeName(appointment?.appointmentType),
          router.locale,
        );

        const unverified = item?.appointment?.unverified;

        const groupAppointment = appointment?.groupAppointment;

        return (
          <List.Item
            className={selected ? styles.listItemSelected : styles.listItem}
            onClick={() => {
              if (appointmentId === item?.appointment?.id) {
                setAppointmentId(null);
              } else {
                setAppointmentId(item.appointment?.id);
              }
            }}
          >
            <List.Item.Meta
              avatar={
                <span>
                  {officeHour?.logoImageId ? (
                    <Avatar src={buildImageDownloadUrl(officeHour.logoImageId)} size={50} />
                  ) : (
                    <Avatar shape="circle" size={50} />
                  )}
                </span>
              }
              title={
                <div className={styles.dateTime}>
                  <div>
                    <CalendarOutlined /> {buildTimestampDate(startTime)}{' '}
                    <ClockCircleOutlined style={{ marginLeft: 15 }} />{' '}
                    {buildTimestampTime(startTime)}
                  </div>
                  <div>
                    <AppointmentStatusTag
                      unverified={unverified}
                      status={getAttendeeStatus(item?.appointment, currentUser?.id)}
                    />
                  </div>
                </div>
              }
              description={
                <div className={styles.listItemFacilities}>
                  <div
                    style={{
                      color: 'black',
                      fontSize: '1.2em',
                    }}
                  >
                    {appointmentTypeName}
                  </div>
                  <AppointmentLocation location={item?.appointment?.location}></AppointmentLocation>
                  {groupAppointment && (
                    <div>
                      <TeamOutlined /> {formatMessage({ id: 'appointment.group-appointment' })}
                    </div>
                  )}
                  <div> {formatPortalFacilityPath(facilities)}</div>
                </div>
              }
            />
          </List.Item>
        );
      }}
      rowKey={(proxyAppointment) => proxyAppointment!.appointment!.id!}
    />
  ) : (
    <Empty
      description={formatMessage({
        id: appointmentFilter === 'upcoming' ? 'appointments.empty' : 'appointments-past.empty',
      })}
    />
  );
};
